import React, { useEffect, useState } from 'react';
import { Form, Input, Alert, Typography, Card, Button, Spin } from 'antd';
import { useLocation } from 'react-router-dom';
import styles from '../../styles/login.module.scss';
import { AuthAPI } from '../../api/auth-api';

const { Title } = Typography;

// Public page for every user (clients included). The API's password-reset email links to
// {API}/reset-password, which redirects here with ?token=&lang=. Clients get the email in their
// own language, so this page carries its own copy instead of the dashboard's Serbian-only text.
const STRINGS = {
  sr: {
    title: 'Nova šifra',
    subtitle: 'Unesite novu šifru za vaš nalog.',
    password: 'Nova šifra',
    confirm: 'Potvrdite šifru',
    required: 'Unesite šifru!',
    rules:
      'Šifra mora imati najmanje 8 karaktera, veliko i malo slovo, broj i specijalni karakter (!@#$%^&*).',
    mismatch: 'Šifre se ne poklapaju.',
    submit: 'Sačuvaj šifru',
    invalidTitle: 'Link je nevažeći ili je istekao',
    invalidBody:
      'Zatražite novi link za promenu šifre iz aplikacije ili web prodavnice.',
    missingToken: 'Nedostaje token za resetovanje šifre.',
    successTitle: 'Uspešno ste promenili šifru!',
    successBody: 'Sada se možete prijaviti sa novom šifrom.',
    genericError: 'Došlo je do greške pri promeni šifre.',
  },
  en: {
    title: 'New password',
    subtitle: 'Enter a new password for your account.',
    password: 'New password',
    confirm: 'Confirm password',
    required: 'Please enter a password!',
    rules:
      'Password must be at least 8 characters and contain an uppercase letter, a lowercase letter, a number and a special character (!@#$%^&*).',
    mismatch: 'Passwords do not match.',
    submit: 'Save password',
    invalidTitle: 'This link is invalid or has expired',
    invalidBody:
      'Request a new password reset link from the app or the web shop.',
    missingToken: 'The password reset token is missing.',
    successTitle: 'Your password has been changed!',
    successBody: 'You can now sign in with your new password.',
    genericError: 'An error occurred while changing the password.',
  },
  hu: {
    title: 'Új jelszó',
    subtitle: 'Adjon meg új jelszót a fiókjához.',
    password: 'Új jelszó',
    confirm: 'Jelszó megerősítése',
    required: 'Kérjük, adja meg a jelszót!',
    rules:
      'A jelszónak legalább 8 karakterből kell állnia, és tartalmaznia kell nagybetűt, kisbetűt, számot és speciális karaktert (!@#$%^&*).',
    mismatch: 'A jelszavak nem egyeznek.',
    submit: 'Jelszó mentése',
    invalidTitle: 'A link érvénytelen vagy lejárt',
    invalidBody:
      'Kérjen új jelszó-visszaállító linket az alkalmazásból vagy a webshopból.',
    missingToken: 'Hiányzik a jelszó-visszaállító token.',
    successTitle: 'A jelszavát sikeresen megváltoztatta!',
    successBody: 'Most már bejelentkezhet az új jelszavával.',
    genericError: 'Hiba történt a jelszó módosítása közben.',
  },
  de: {
    title: 'Neues Passwort',
    subtitle: 'Geben Sie ein neues Passwort für Ihr Konto ein.',
    password: 'Neues Passwort',
    confirm: 'Passwort bestätigen',
    required: 'Bitte geben Sie ein Passwort ein!',
    rules:
      'Das Passwort muss mindestens 8 Zeichen lang sein und einen Groß- und einen Kleinbuchstaben, eine Zahl sowie ein Sonderzeichen (!@#$%^&*) enthalten.',
    mismatch: 'Die Passwörter stimmen nicht überein.',
    submit: 'Passwort speichern',
    invalidTitle: 'Der Link ist ungültig oder abgelaufen',
    invalidBody:
      'Fordern Sie in der App oder im Webshop einen neuen Link zum Zurücksetzen an.',
    missingToken: 'Das Token zum Zurücksetzen des Passworts fehlt.',
    successTitle: 'Ihr Passwort wurde geändert!',
    successBody: 'Sie können sich jetzt mit Ihrem neuen Passwort anmelden.',
    genericError: 'Beim Ändern des Passworts ist ein Fehler aufgetreten.',
  },
};

// Same rules as the API's ResetPasswordDto.
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/;

const ResetPassword = () => {
  const query = new URLSearchParams(useLocation().search);
  const token = query.get('token');
  const lang = STRINGS[query.get('lang')] ? query.get('lang') : 'sr';
  const t = STRINGS[lang];

  // checking -> form | invalid ; form -> done
  const [status, setStatus] = useState(token ? 'checking' : 'invalid');
  const [invalidMessage, setInvalidMessage] = useState(
    token ? '' : t.missingToken
  );
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    AuthAPI.validateResetToken({ token, lang }).then(({ success, message }) => {
      if (cancelled) return;
      if (success) {
        setStatus('form');
      } else {
        setInvalidMessage(message);
        setStatus('invalid');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token, lang]);

  const handleFinish = async ({ password }) => {
    setError('');
    setSubmitting(true);
    const { success, message } = await AuthAPI.resetPassword({
      password,
      passwordToken: token,
      lang,
    });
    setSubmitting(false);

    if (success) {
      setStatus('done');
    } else {
      setError(message || t.genericError);
    }
  };

  const renderContent = () => {
    if (status === 'checking') {
      return <Spin />;
    }

    if (status === 'invalid') {
      return (
        <>
          <Title className={styles.login__title} level={1}>
            {t.invalidTitle}
          </Title>
          {invalidMessage && (
            <Alert
              message={invalidMessage}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <p className={styles.login__subtitle}>{t.invalidBody}</p>
        </>
      );
    }

    if (status === 'done') {
      return (
        <>
          <img
            src="/assets/icons/check.svg"
            alt="Success"
            width={48}
            height={48}
            style={{ marginBottom: 24 }}
          />
          <Title className={styles.login__title} level={1}>
            {t.successTitle}
          </Title>
          <p className={styles.login__subtitle}>{t.successBody}</p>
        </>
      );
    }

    return (
      <>
        <img
          src="/assets/icons/new-pass.svg"
          alt="New password"
          width={48}
          height={48}
          style={{ marginBottom: 24 }}
        />
        <Title className={styles.login__title} level={1}>
          {t.title}
        </Title>
        <p className={styles.login__subtitle}>{t.subtitle}</p>

        <Form layout="vertical" onFinish={handleFinish}>
          <Form.Item
            label={t.password}
            name="password"
            extra={t.rules}
            rules={[
              { required: true, message: t.required },
              { pattern: PASSWORD_PATTERN, message: t.rules },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            label={t.confirm}
            name="confirm"
            dependencies={['password']}
            rules={[
              { required: true, message: t.required },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return !value || getFieldValue('password') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error(t.mismatch));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item className={styles.login__submit}>
            <Button
              type="primary"
              htmlType="submit"
              className="btn-primary"
              loading={submitting}
            >
              {t.submit}
            </Button>
          </Form.Item>
        </Form>
      </>
    );
  };

  return (
    <div className={styles.login}>
      <div className={styles.login__left}>
        <div className={styles.login__formCard}>
          <Card className={styles.login__card}>{renderContent()}</Card>
        </div>
      </div>
      <div className={styles.login__right}></div>
    </div>
  );
};

export default ResetPassword;
